import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Code2, Film, Menu, Sparkles, Workflow, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.png";
import { PortfolioCategoryId, portfolioCategories } from "@/data/portfolioData";

const portfolioIcons = { web: Code2, ugc: Sparkles, automation: Workflow, video: Film } as const;
const menuFocus =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const navLinks: { label: string; to: string; dropdown?: boolean }[] = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "Results", to: "/results" },
  { label: "Portfolio", to: "/portfolio", dropdown: true },
  { label: "Blog", to: "/blog" },
  { label: "Newsletter", to: "#newsletter" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [mobilePortfolioOpen, setMobilePortfolioOpen] = useState(false);
  const portfolioRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPortfolioOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!portfolioOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (portfolioRef.current && !portfolioRef.current.contains(e.target as Node)) setPortfolioOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPortfolioOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [portfolioOpen]);

  const goPortfolio = (id: PortfolioCategoryId) => {
    setPortfolioOpen(false);
    setOpen(false);
    setMobilePortfolioOpen(false);
    navigate(`/portfolio?category=${id}`);
    window.scrollTo({ top: 0 });
  };

  const isActive = (to: string) => {
    if (to === "#newsletter") return false;
    if (to === "/") return pathname === "/";
    return pathname.startsWith(to);
  };

  const handleNavClick = (to: string) => {
    if (to === "#newsletter") {
      setOpen(false);
      // Delay scroll to allow mobile menu to close first
      setTimeout(() => {
        const el = document.getElementById("newsletter");
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        }
      }, 300);
      return true;
    }
    return false;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border" style={{ background: "rgba(10, 14, 26, 0.8)", backdropFilter: "blur(20px) saturate(180%)" }}>
      <div className="container flex items-center justify-between h-[80px] md:h-[84px]">
        <Link to="/" className="flex items-center gap-2 shrink-0 -my-4">
          <img src={logo} alt="Xentic Solutions" className="h-[100px] md:h-[100px] xl:h-[120px] w-auto -mx-5 xl:-mx-6 invert brightness-200 hue-rotate-180 drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]" />
        </Link>

        {/* Desktop */}
        <nav className="hidden lg:flex items-center gap-0 xl:gap-1">
          {navLinks.map((l) => (
            l.dropdown ? (
              <div
                key={l.to}
                ref={portfolioRef}
                className="relative"
                onMouseEnter={() => setPortfolioOpen(true)}
                onMouseLeave={() => setPortfolioOpen(false)}
              >
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={portfolioOpen}
                  onClick={() => setPortfolioOpen((v) => !v)}
                  className={`relative inline-flex items-center gap-1 px-2 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-sm font-medium transition-all duration-300 cursor-pointer ${menuFocus} ${
                    isActive(l.to) || portfolioOpen ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${portfolioOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                <AnimatePresence>
                  {portfolioOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                    >
                      <div
                        role="menu"
                        aria-label="Portfolio categories"
                        className="w-60 rounded-2xl border border-border p-2 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
                        style={{ background: "#0a0e1a" }}
                      >
                        {portfolioCategories.map((c) => {
                          const Icon = portfolioIcons[c.id];
                          return (
                            <button
                              key={c.id}
                              type="button"
                              role="menuitem"
                              onClick={() => goPortfolio(c.id)}
                              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer ${menuFocus}`}
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                                <Icon className="h-4 w-4" aria-hidden="true" />
                              </span>
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : l.to === "#newsletter" ? (
              <button
                key={l.to}
                onClick={() => handleNavClick(l.to)}
                className="relative px-2 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-sm font-medium transition-all duration-300 text-muted-foreground hover:text-foreground"
              >
                {l.label}
              </button>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                className={`relative px-2 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-sm font-medium transition-all duration-300 ${
                  isActive(l.to)
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
                {isActive(l.to) && (
                  <motion.div
                    layoutId="nav-active"
                    className="absolute -bottom-1 left-[20%] right-[20%] h-[2.5px] rounded-full"
                    style={{
                      background: "linear-gradient(90deg, transparent, hsl(189 100% 50%), hsl(243 76% 59%), transparent)",
                    }}
                    transition={{ type: "spring", stiffness: 380, damping: 28 }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(90deg, transparent, hsl(189 100% 50%), hsl(243 76% 59%), transparent)",
                        filter: "blur(4px)",
                      }}
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </motion.div>
                )}
              </Link>
            )
          ))}
        </nav>

        <Button
          className="hidden lg:inline-flex group relative overflow-hidden border-0 hover:-translate-y-0.5 transition-transform duration-300 px-4 xl:px-5 py-2 text-[13px] xl:text-sm whitespace-nowrap shrink-0"
          style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
          onClick={() => navigate("/contact")}
        >
          <span className="relative z-10 text-white font-semibold tracking-normal">Book Free Consultation →</span>
        </Button>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 text-foreground"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden border-b border-border"
            style={{ background: "rgba(10, 14, 26, 0.95)", backdropFilter: "blur(20px)" }}
          >
            <div className="px-4 pb-4 space-y-1 pt-2">
              {navLinks.map((l) => (
                l.dropdown ? (
                  <div key={l.to}>
                    <button
                      type="button"
                      aria-expanded={mobilePortfolioOpen}
                      onClick={() => setMobilePortfolioOpen((v) => !v)}
                      className={`flex w-full items-center justify-between py-2.5 px-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${menuFocus} ${
                        isActive(l.to) ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                    >
                      {l.label}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobilePortfolioOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                    <AnimatePresence initial={false}>
                      {mobilePortfolioOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-1 mb-1 ml-3 space-y-1 border-l border-border pl-2">
                            {portfolioCategories.map((c) => {
                              const Icon = portfolioIcons[c.id];
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => goPortfolio(c.id)}
                                  className={`flex w-full items-center gap-3 min-h-12 rounded-lg px-3 text-left text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer ${menuFocus}`}
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                                    <Icon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                  {c.label}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : l.to === "#newsletter" ? (
                  <button
                    key={l.to}
                    onClick={() => handleNavClick(l.to)}
                    className="block w-full text-left py-2.5 px-3 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                  >
                    {l.label}
                  </button>
                ) : (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`block py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(l.to)
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                  >
                    {l.label}
                  </Link>
                )
              ))}
              <Button
                className="w-full mt-2 border-0 px-5 py-2.5 text-sm whitespace-nowrap"
                style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                onClick={() => { setOpen(false); navigate("/contact"); }}
              >
                <span className="text-white font-semibold tracking-normal">Book Free Consultation →</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
